import React, { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useNavigate, Router, redirect, Navigate } from "react-router-dom";
import Layout from "pages/auth/Layout";
import Dashboard from "pages/dashboard/Dashboard";
import Login from "pages/auth/Login";
import Profile from "pages/profile/Profile";
import Sidebar from "pages/sidebar/index";
import DefaultLayout from "pages/layout/DefaultLayout";
import routes from "routes";
import Loading from "components/Loading";
import { getCookie } from "services/cookies";
import { AxiosInterceptor } from "services/axios";
import useLoginStore from "hooks/useLoginStore";
import { DRAFT_KONTRAK_PATH } from "utils/paths";

// Komponen penjaga didefinisikan di luar App. Kalau didefinisikan di dalam,
// identitasnya berubah tiap App re-render dan React akan me-remount seluruh
// halaman di bawahnya setiap kali ada perubahan state.
const Protected = ({ path, children }) => {
  const isSignedIn = getCookie('auth_token');

  if (path.includes('auth')) {
    if (isSignedIn) {
      return <Navigate to="/" replace />
    }
    return children
  } else {
    if (!isSignedIn) {
      return <Navigate to="/auth/login" replace />
    }
    return children
  }
}

/**
 * Penjagaan halaman berdasarkan peran. Peran dibaca dari `serverProfile`
 * (hasil GET /api/user/profile), bukan dari localStorage, supaya tidak bisa
 * dilewati dengan mengubah data di devtools. Halaman baru dirender setelah
 * peran dipastikan.
 */
const RoleProtected = ({ deniedRoles, children }) => {
  const serverProfile = useLoginStore((state) => state.serverProfile);
  const isLoadingServerProfile = useLoginStore((state) => state.isLoadingServerProfile);
  const hasResolvedServerProfile = useLoginStore((state) => state.hasResolvedServerProfile);

  if (!deniedRoles || deniedRoles.length === 0) return children;
  if (isLoadingServerProfile || !hasResolvedServerProfile) return <Loading />;

  const isDenied = deniedRoles.some((role) => role == serverProfile?.role_code);
  if (isDenied) return <Navigate to="/" replace />;

  return children;
}

/**
 * Penjagaan keanggotaan. Berlaku untuk SEMUA halaman setelah login, bukan
 * hanya yang bertanda `onboardingOnly`.
 *
 * Anggota yang belum diaktivasi admin (belum punya `mamboo_id`) hanya boleh
 * membuka halaman masa onboarding. Sebaliknya, begitu `mamboo_id` terbit,
 * halaman onboarding tidak relevan lagi dan ditutup.
 *
 * Keputusannya diambil saat render, bukan di dalam useEffect. Dulu pengalihan
 * ini dilakukan oleh efek di ProfileCompletionGate, yang baru berjalan setelah
 * halaman sempat dirender — sehingga anggota non-member melihat dashboard
 * dulu sekejap sebelum dialihkan.
 *
 * Selama status keanggotaan belum pasti, tidak ada halaman yang dirender sama
 * sekali. `hasResolvedServerProfile` yang membedakan "sedang/belum diambil"
 * dari "sudah diambil dan hasilnya kosong"; tanpa itu, alur login yang lupa
 * memanggil fetchServerProfile akan lolos begitu saja.
 */
const MemberProtected = ({ onboardingOnly, children }) => {
  const serverProfile = useLoginStore((state) => state.serverProfile);
  const isLoadingServerProfile = useLoginStore((state) => state.isLoadingServerProfile);
  const hasResolvedServerProfile = useLoginStore((state) => state.hasResolvedServerProfile);
  const fetchServerProfile = useLoginStore((state) => state.fetchServerProfile);

  // Jaring pengaman: sesi sudah ada tapi profilnya belum pernah diambil.
  // Diambil sekali di sini; `hasResolvedServerProfile` menahan efek ini agar
  // tidak berulang tanpa henti kalau permintaannya gagal.
  useEffect(() => {
    if (!isLoadingServerProfile && !hasResolvedServerProfile) {
      fetchServerProfile();
    }
  }, [isLoadingServerProfile, hasResolvedServerProfile, fetchServerProfile]);

  if (isLoadingServerProfile || !hasResolvedServerProfile) return <Loading />;

  // Profil gagal diambil pun diperlakukan sebagai belum-member (fail-closed):
  // lebih baik tertahan di halaman onboarding daripada fitur ikut terbuka.
  const isMember = Boolean(serverProfile?.mamboo_id);

  if (!isMember && !onboardingOnly) {
    return <Navigate to={DRAFT_KONTRAK_PATH} replace />;
  }

  if (isMember && onboardingOnly) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  // Selector dipakai supaya App tidak ikut re-render tiap ada perubahan store.
  const fetchServerProfile = useLoginStore((state) => state.fetchServerProfile);

  useEffect(() => {
    if (getCookie('auth_token')) {
      fetchServerProfile();
    }
  }, [fetchServerProfile]);

  return (
    <BrowserRouter>
      {/* <React.Suspense
        fallback={<Loading />}
      > 
      gak dipake soalnya importnya enggak lazy import
      */}
      <Routes>
        {routes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={
              route.path.includes('auth') ?
              <AxiosInterceptor>
                <Protected path={route.path}>
                  <Layout children={<route.component />} />
                </Protected>
              </AxiosInterceptor> :
                <Protected path={route.path}>
                  <AxiosInterceptor>
                    <RoleProtected deniedRoles={route.deniedRoles}>
                      <MemberProtected onboardingOnly={route.onboardingOnly}>
                        <DefaultLayout children={<route.component />} />
                      </MemberProtected>
                    </RoleProtected>
                  </AxiosInterceptor>
                </Protected>
            }
          />
        ))}
      </Routes>
      {/* </React.Suspense> */}
    </BrowserRouter>
  );
}

export default App;
